import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, List, ArrowLeft } from 'lucide-react';
import { useSupportChat } from '../../contexts/SupportChatContext';
import { useAgentChat } from '../../contexts/AgentChatContext';
import { useAuth } from '../../contexts/AuthContext';
import { useAppFeatures } from '../../contexts/AppFeaturesContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import ChatWelcome from './ChatWelcome';
import TypingIndicator from './TypingIndicator';
import ConversationList from './ConversationList';
import AgentChatContent from '../AgentChat/AgentChatContent';
import AgentsIframeContent from '../AgentChat/AgentsIframeContent';
import { usePermissions } from '../../utils/usePermissions';

const ChatPanel: React.FC = () => {
  const {
    isOpen,
    close,
    messages,
    conversationStatus,
    supportRequestId,
    isPending,
    pollElapsed,
    sendMessage,
    activeView,
    setActiveView,
  } = useSupportChat();

  const { activeTab, setActiveTab } = useAgentChat();
  const { profile } = useAuth();
  const { ai_agent: aiAgentEnabled } = useAppFeatures();
  const { hasPermission } = usePermissions();
  const canSeeAgentsIframe = !!profile?.is_admin && hasPermission('ai_chat:view');
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isPending]);

  // Focus trap — Escape closes
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, close]);

  if (!isOpen) return null;

  const showChat = activeView === 'chat';
  const isConversationEnded = conversationStatus === 'escalated' || conversationStatus === 'closed_by_user' || conversationStatus === 'abandoned';
  const contextChip = getContextChip();

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Чат с AI помощником"
      style={{
        position: 'fixed',
        ...(isMobile ? {
          top: 0, left: 0, right: 0, bottom: 0,
          width: '100vw',
          height: '100vh',
          borderRadius: 0,
        } : {
          bottom: 88,
          right: 24,
          width: 420,
          height: 600,
          borderRadius: 'var(--radius-xl)',
        }),
        background: 'var(--surface-1, white)',
        boxShadow: 'var(--shadow-xl)',
        zIndex: 1050,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        animation: 'supportChatPanelOpen 0.25s ease-out',
        transformOrigin: 'bottom right',
      }}
    >
      {/* Header */}
      <div className="flex flex-col-dir flex-shrink-0" style={{ background: 'linear-gradient(135deg, var(--primary-600) 0%, var(--primary-700) 100%)', color: 'var(--surface-1, white)' }}>
        <div className="flex items-center justify-between" style={{ padding: 'var(--space-2) var(--space-4) 0' }}>
          <div className="flex-center-g1">
            {isMobile && (
              <button
                type="button"
                onClick={close}
                style={headerBtnStyle}
                aria-label="Назад"
              >
                <ArrowLeft size={16} />
              </button>
            )}
            {/* Tabs */}
            <div role="tablist" className="flex gap-1">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'support'}
                onClick={() => setActiveTab('support')}
                style={{
                  ...tabStyle,
                  background: activeTab === 'support' ? 'rgba(255,255,255,0.25)' : 'transparent',
                }}
              >
                Поддержка
              </button>
              {aiAgentEnabled && profile?.is_admin && (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'agent'}
                  aria-label="AI Агент"
                  onClick={() => setActiveTab('agent')}
                  style={{
                    ...tabStyle,
                    background: activeTab === 'agent' ? 'rgba(255,255,255,0.25)' : 'transparent',
                  }}
                >
                  AI Агент
                </button>
              )}
              {canSeeAgentsIframe && (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'agents-iframe'}
                  aria-label="AI Ассистент"
                  onClick={() => setActiveTab('agents-iframe')}
                  style={{
                    ...tabStyle,
                    background: activeTab === 'agents-iframe' ? 'rgba(255,255,255,0.25)' : 'transparent',
                  }}
                >
                  AI Ассистент
                </button>
              )}
            </div>
          </div>
          <div className="flex gap-1">
            {!isMobile && (
              <button type="button" onClick={close} style={headerBtnStyle} aria-label="Закрыть">
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {activeTab === 'support' && contextChip && showChat && (
          <div style={{
            padding: 'var(--space-1) var(--space-4) var(--space-2)',
          }}>
            <span className="d-inline-block rounded-full text-xs" style={{ padding: '2px var(--space-2)', background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.9)' }}>
              📍 {contextChip}
            </span>
          </div>
        )}
      </div>

      {/* Body */}
      {activeTab === 'agents-iframe' ? (
        <AgentsIframeContent />
      ) : activeTab === 'agent' ? (
        <AgentChatContent />
      ) : showChat ? (
        <>
          {/* Sub-nav: switch to conversation list */}
          <div className="flex items-center justify-end flex-shrink-0" style={{ padding: 'var(--space-2) var(--space-4) 0' }}>
            <button
              type="button"
              onClick={() => setActiveView('list')}
              className="flex items-center gap-1 bg-gray-100 border-none rounded-md cursor-pointer text-xs text-gray-500" style={{ padding: '2px var(--space-2)', fontFamily: 'inherit' }}
            >
              <List size={12} />
              Беседы
            </button>
          </div>
          <div className="flex-1 overflow-y-auto flex flex-col-dir">
            {messages.length === 0 && !isPending ? (
              <ChatWelcome
                userName={profile?.first_name || profile?.full_name?.split(' ')[0]}
                onTopicClick={(topic) => sendMessage(topic)}
              />
            ) : (
              <div className="flex flex-col-dir gap-3 py-4">
                {messages.map(msg => (
                  <ChatMessage
                    key={msg.id}
                    role={msg.role}
                    content={msg.content}
                    supportRequestId={msg.role === 'system' ? supportRequestId : undefined}
                    attachments={msg.attachments}
                  />
                ))}
                {isPending && <TypingIndicator elapsed={pollElapsed} />}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input — disabled when conversation ended */}
          {!isConversationEnded ? (
            <ChatInput
              onSend={sendMessage}
              disabled={isPending}
            />
          ) : (
            <div className="border-t-gray-200 px-4-py-3 flex flex-col-dir items-center gap-2">
              <span className="text-gray-500 text-xs">
                Диалог завершён
              </span>
              {supportRequestId && (
                <button
                  className="btn-primary w-full justify-center text-sm"
                  onClick={() => {
                    close();
                    navigate(`/support/${supportRequestId}`);
                  }}
                >
                  Перейти к обращению →
                </button>
              )}
            </div>
          )}
        </>
      ) : (
        <ConversationList />
      )}
    </div>
  );
};

const tabStyle: React.CSSProperties = {
  padding: 'var(--space-1) var(--space-3)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  color: 'var(--white)',
  cursor: 'pointer',
  fontSize: 'var(--text-sm)',
  fontWeight: 500,
  fontFamily: 'inherit',
  transition: 'background 0.15s',
  minHeight: 32,
};

const headerBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  background: 'rgba(255,255,255,0.15)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  color: 'var(--surface-1, white)',
  cursor: 'pointer',
  transition: 'background 0.15s',
};

function getContextChip(): string | null {
  const path = window.location.pathname;
  const segments = path.split('/').filter(Boolean);
  if (segments.length === 0) return null;

  const labels: Record<string, string> = {
    agreements: 'Договоры',
    customers: 'Клиенты',
    tickets: 'Тикеты',
    leads: 'Лиды',
    support: 'Поддержка',
    admin: 'Админ',
    settings: 'Настройки',
  };

  const base = labels[segments[0]] ?? segments[0];
  const id = segments[1] && /^\d+$/.test(segments[1]) ? `#${segments[1]}` : '';
  return `${base}${id ? ` / ${id}` : ''}`;
}

export default ChatPanel;
